import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../components/Whatsapp/Template/template.constant.js';

/** @typedef {'image' | 'video' | 'audio' | 'document'} AttachmentKind */

export const ATTACHMENT_ACCEPT = {
  image: WHATSAPP_ALLOWED_MIME.image.join(','),
  video: WHATSAPP_ALLOWED_MIME.video.join(','),
  audio: [
    ...WHATSAPP_ALLOWED_MIME.audio,
    '.mp3',
    '.aac',
    '.m4a',
    '.amr',
    '.ogg',
    '.opus',
    '.webm',
    '.wav',
  ].join(','),
  document: [
    ...WHATSAPP_ALLOWED_MIME.file,
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.csv',
  ].join(','),
};

export const ATTACHMENT_HINTS = {
  image: 'JPEG, PNG · max 5 MB',
  video: 'MP4, 3GP · max 16 MB',
  audio: 'MP3, AAC, MP4, AMR, OGG, WEBM · max 16 MB',
  document: 'PDF, Office, TXT · less than 95 MB',
};

function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

function getExtension(fileName = '') {
  const parts = String(fileName).toLowerCase().split('.');
  if (parts.length < 2) return '';
  return `.${parts.pop()}`;
}

const EXTENSION_KIND_MAP = {
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.mp4': 'video',
  '.3gp': 'video',
  '.3gpp': 'video',
  '.mp3': 'audio',
  '.aac': 'audio',
  '.amr': 'audio',
  '.ogg': 'audio',
  '.opus': 'audio',
  '.webm': 'audio',
  '.wav': 'audio',
  '.m4a': 'audio',
  '.pdf': 'document',
  '.doc': 'document',
  '.docx': 'document',
  '.xls': 'document',
  '.xlsx': 'document',
  '.ppt': 'document',
  '.pptx': 'document',
  '.txt': 'document',
  '.csv': 'document',
};

function inferAttachmentKind(mime, ext = '') {
  const normalized = String(mime || '').toLowerCase().split(';')[0].trim();
  if (normalized) {
    if (WHATSAPP_ALLOWED_MIME.image.includes(normalized)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(normalized)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(normalized) || normalized.startsWith('audio/')) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(normalized)) return 'document';
  }
  if (ext && EXTENSION_KIND_MAP[ext]) {
    return EXTENSION_KIND_MAP[ext];
  }
  return null;
}

/**
 * WhatsApp Cloud API media rules for intervene attachments.
 * @param {File | null | undefined} file
 * @param {AttachmentKind | null} [expectedKind]
 */
export function validateAttachmentFile(file, expectedKind = null) {
  if (!file) {
    return { valid: false, message: 'No file selected' };
  }

  if (!file.size || file.size <= 0) {
    return {
      valid: false,
      message: 'File cannot be 0 MB (empty file). Please select a valid file.',
    };
  }

  const ext = getExtension(file.name);
  if (ext && BLOCKED_EXTENSIONS.has(ext)) {
    return {
      valid: false,
      message: `"${ext}" files are not allowed for security reasons.`,
    };
  }

  const kind = inferAttachmentKind(file.type, ext);
  if (!kind) {
    return {
      valid: false,
      message: expectedKind
        ? `Unsupported ${expectedKind} format (${file.type || ext || 'unknown'}).`
        : `Unsupported file type (${file.type || ext || 'unknown'}).`,
    };
  }

  if (expectedKind && kind !== expectedKind) {
    const hint = ATTACHMENT_HINTS[expectedKind];
    return {
      valid: false,
      message: hint
        ? `Please choose a valid ${expectedKind} file (${hint}).`
        : `Please choose a valid ${expectedKind} file.`,
    };
  }

  const limitKey = kind === 'document' ? 'file' : kind;
  const limit = WHATSAPP_LIMITS[limitKey];
  if (limit && file.size > limit) {
    return {
      valid: false,
      message: `${kind[0].toUpperCase()}${kind.slice(1)} too large. Max ${bytesToMB(limit)} MB (yours is ${bytesToMB(file.size)} MB).`,
    };
  }

  return { valid: true, kind };
}

export function getAttachmentAccept(kind) {
  return ATTACHMENT_ACCEPT[kind] || '';
}
